// React
import React, { useState, useMemo, useEffect, useContext } from "react";

// Material UI
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    AppBar,
    Toolbar,
    makeStyles,
    CircularProgress,
    Divider,
} from "@material-ui/core";
import NotificationContext from "views/Components/Context.js";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";
// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { stepperStyles, QontoConnector } from "views/Styles/stepperStyles";
import appbarStyles from "views/Styles/appbarStyles";

import ReviewScreen from "../ReviewScreen";
// NEW: import your step titles/descriptions
import stepLabels from "./stepLabels";
import ProjectDetailsScreen from "./ProjectDetailsScreen";
// Model templates (names + fetch by name)
import { modelTemplateNames, fetchTemplateMd } from "../Model Development/modelTemplates";
import {
    complianceTemplates,
    fetchComplianceMdByName,
} from "../Model Development/complianceTemplates";
import { validationTemplateNames, fetchValidationMd } from "./validationTemplates"
import {
    getModelValidationProjectDetails,
    updateModelValidationProjectDetails,
    setNextStepValidation,
    saveNewVersionValidation,
    generateOverviewValidation,
    generateModelValidationPlan,
    publishProjectValidation
} from "views/APIs/ToolboxUtility";
import { fetchAllProjectsAPI } from "views/APIs/ProjectsUtility";


const useStyles = makeStyles((theme) => ({
    ...styles,
    ...stepperStyles(theme),
    ...appbarStyles(theme),
}));

export function getVersionOptions(doc, markdownName) {
    const section = doc[markdownName];
    if (!Array.isArray(section)) return [];

    return section.map(e => e.version);
}

function getMarkdownEntry(doc, markdownName, version) {
    if (version === 0) version = 1; // default to version 1 if 0 is selected
    const section = doc?.[markdownName];
    if (!Array.isArray(section)) return "";
    const entry = section[version - 1];
    return entry ? entry['markdown'] : "";
}

function getMarkdownName(activeStep) {
    const map = {
        1: "model_validation_plan_md",
        2: "model_description_md",
        3: "system_use_case_md",
        4: "regulatory_compliance_md",
        5: "overview_md",
        6: "final_plan_md",
    };

    return map[Number(activeStep)] || "";
}


function getFileList(doc) {
    if (!doc || !Array.isArray(doc.uploaded_files)) return [];

    return doc.uploaded_files.map(file => ({
        id: file.file_id,
        name: file.name
    }));
}

const LoadingIcon = () => <CircularProgress size={22} />;

export default function ValidationBoard(props) {
    const { projectId } = props.match.params;
    const [activeStep, setActiveStep] = useState(0);
    const [markdownContent, setMarkdownContent] = useState("placeholder");
    const classes = useStyles();
    const [projectList, setProjectList] = useState([])
    const [modelProject, setModelProject] = useState({})
    const [projectName, setProjectName] = useState("Loading...")
    const [projectDescription, setProjectDescription] = useState("");
    const [associatedProjectId, setAssociatedProjectId] = useState("")
    const [fileList, setFileList] = useState([])
    const [versionOptions, setVersionOptions] = useState([]);
    const [version, setVersion] = useState(0);
    const [maxStep, setMaxStep] = useState(0);
    const [loading, setLoading] = useState({
        show: false,
        message: "",
        type: "",
    });
    const { showNotification } = useContext(NotificationContext);
    const [originalMarkdownContent, setOriginalMarkdownContent] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const resp = await getModelValidationProjectDetails(projectId);
                if (!alive) return;
                setModelProject(resp);
                setProjectName(resp.title || "Unnamed Project");
                setProjectDescription(resp.description || "");
                setAssociatedProjectId(resp.associated_project_id || "");
                setActiveStep(resp.step || 0);
                setFileList(getFileList(resp));
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; }; // proper cleanup function
    }, [projectId]);


    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const resp = await fetchAllProjectsAPI();
                if (!alive) return;
                setProjectList(resp || []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; };
    }, [projectId]);

    useEffect(() => {
        if (activeStep > 0) {
            setVersionOptions(modelProject[getMarkdownName(activeStep)] || []);
        }
    }, [activeStep]);

    useEffect(() => {
        setVersion(versionOptions.length > 0 ? versionOptions[versionOptions.length - 1].version : 0)
    }, [versionOptions]);

    useEffect(() => {
        if (version !== null) {
            setMarkdownContent(getMarkdownEntry(modelProject, getMarkdownName(activeStep), version) || "");
            setOriginalMarkdownContent(getMarkdownEntry(modelProject, getMarkdownName(activeStep), version) || "");
        }
    }, [version, activeStep]);

    useEffect(() => {
        setMaxStep(modelProject?.step || 0);
    }, [modelProject]);

    const handleStepClick = (index) => {
        if (index > maxStep) return;
        setActiveStep(index);
    }

    const onDropDownSelection = async (value) => {
        let md = "";
        if (value.length === 0) {
            md = ""
        }
        else if (activeStep === 1) {
            md = await fetchValidationMd(value[value.length - 1]);
        }
        else if (activeStep === 2) {
            md = await fetchTemplateMd(value[value.length - 1]);
        } else if (activeStep === 4) {
            md = await fetchComplianceMdByName(value[value.length - 1]);
        }

        setMarkdownContent(md);
    };

    const dropdownOptions = useMemo(() => {
        if (activeStep === 2) {
            return modelTemplateNames;
        }
        if (activeStep === 4) return complianceTemplates
        return [];
    }, [activeStep]);
    const meta = stepLabels[activeStep]
    const isMultiSelect = false;

    const nextLabel = activeStep === 6 ? "Publish" : "Next"


    const onSave = async () => {
        try {
            const mdType = getMarkdownName(activeStep);
            if (mdType) {
                const res = await saveNewVersionValidation(projectId, mdType, markdownContent);
                setModelProject(res);

                // Update version options from the API response
                const newVersionOptions = res[mdType] || [];
                setVersionOptions(newVersionOptions);

                // Set to the latest version
                const latestVersion = newVersionOptions.length > 0 ? newVersionOptions[newVersionOptions.length - 1].version : version + 1;
                setVersion(latestVersion);

                showNotification("Version saved successfully!", "success");
            }
        } catch (e) {
            console.error("Error saving version:", e.message);
            showNotification(`Error saving version: ${e.message}`, "error");
        }
    }

    const onNext = async () => {
        try {
            setLoading({ show: true, message: "Processing your files. This might take a few minutes..." });

            if (activeStep < 6) {
                if (activeStep === 0)
                    await onUpdate();
                else if (markdownContent !== getMarkdownEntry(modelProject, getMarkdownName(activeStep), version)?.markdown)
                    await onSave();

                

                // Then handle API calls that require loading
                if (activeStep === 4) {
                    setLoading({ show: true, message: "Generating Overview. This might take a few minutes..." });
                    const res = await generateOverviewValidation(projectId);
                    setModelProject(res);
                }
                else if (activeStep === 5) {
                    setLoading({ show: true, message: "Generating Model Validation Plan. This might take a few minutes..." });
                    const res = await generateModelValidationPlan(projectId);
                    setModelProject(res);
                }

                // Update stepper position first
                if (maxStep < 6) {
                    if (activeStep === maxStep) {
                        setMaxStep(activeStep + 1);
                    }
                }
                setActiveStep(activeStep + 1);
                const data = await setNextStepValidation(projectId, activeStep + 1);
                setMaxStep(activeStep + 1);
            }
            if (activeStep === 6) {
                const res = await publishProjectValidation(projectId);
                showNotification("Project published successfully!", "success");
            }
            setLoading({ show: false, message: "" });
        } catch (e) {
            setLoading({ show: false, message: "" });
        }
    }
    const onUpdate = async () => {
        modelProject.title = projectName;
        modelProject.description = projectDescription;
        modelProject.associated_project_id = associatedProjectId;
        const res = await updateModelValidationProjectDetails(projectId, modelProject);
        setModelProject(res);
    }

    return (
        <Box>
            <Divider style={{ marginBottom: 16 }} />
            <HeadingWithDescription
                title={projectName || "Unnamed Project"}
                shortDescription={projectDescription}
                identifier="validation"
                showEditIcon={false}
                dialogTitle="Edit Project Details"
                onChange={async (updatedData) => {
                    setProjectName(updatedData.title);
                    setProjectDescription(updatedData.shortDescription);
                    modelProject.title = updatedData.title;
                    modelProject.description = updatedData.shortDescription;
                    await updateModelValidationProjectDetails(projectId, modelProject);
                    showNotification("Project details updated successfully", "success");
                }}
            />

            <AppBar 
                position="static" 
                className={classes.header}
                style={{
                    border: "2px solid #20677b",
                    borderRadius: "6px",
                    margin: 0,
                    marginTop: -16,
                    padding: 0,
                    minHeight: "40px",
                }}
            >
                <Toolbar style={{ minHeight: "40px", padding: "4px 8px" }}>
                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                        {/* Stepper: numbered circles + step labels */}
                        <Stepper
                            activeStep={activeStep}
                            connector={<QontoConnector />}
                            size="small"
                            className={classes.stepperRoot}
                            style={{ 
                                flexGrow: 1,
                                margin: 0,
                                padding: "4px 0"
                            }}
                        >
                            {stepLabels.map((s, index) => {
                                const isDisabled = index > maxStep;
                                return (
                                    <Step 
                                        key={`${s.step_title}-${index}`} 
                                        onClick={() => handleStepClick(index)}
                                        style={{ padding: "0 4px" }}
                                    >
                                        <StepLabel
                                            StepIconComponent={loading.show && index === activeStep ? LoadingIcon : undefined}
                                            StepIconProps={!isDisabled ? {
                                                classes: {
                                                    root: classes.stepIcon,
                                                    active: classes.activeStepIcon,
                                                    completed: classes.completedStepIcon,
                                                },
                                            } : {}}
                                            sx={{
                                                "& .MuiStepLabel-label": {
                                                    display: "none",
                                                },
                                                "&:hover .MuiStepLabel-label": {
                                                    display: "block",
                                                    position: "absolute",
                                                    backgroundColor: "#20677b",
                                                    color: "white",
                                                    padding: "4px 8px",
                                                    borderRadius: "4px",
                                                    fontSize: "0.7rem",
                                                    whiteSpace: "nowrap",
                                                    marginTop: "8px",
                                                    zIndex: 1000,
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                                },
                                                "& .MuiStepLabel-iconContainer": {
                                                    paddingRight: 0,
                                                }
                                            }}
                                        >
                                            {s.step_title}
                                        </StepLabel>
                                    </Step>
                                )
                            })}
                        </Stepper>
                    </Box>
                </Toolbar>
            </AppBar>

            {loading.show ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress className={classes.loaderClass} />
                    <Typography variant="h6" style={{ marginLeft: 16, textTransform: "none" }}>
                        {loading.message}
                    </Typography>
                </Box>
            ) : activeStep === 0 ? (
                <ProjectDetailsScreen
                    name={projectName}
                    setName={setProjectName}
                    onNext={onNext}
                    description={projectDescription}
                    setDescription={setProjectDescription}
                    projectsList={projectList}
                    associatedProjectId={associatedProjectId}
                    setAssociatedProjectId={setAssociatedProjectId}
                    onUpdate={onUpdate}
                    projectId={projectId}
                    fileList={fileList}
                    setFileList={setFileList}
                />
            ) : (
                <Box pt={2}>
                    <ReviewScreen
                        title={meta.step_title}
                        description={meta.step_description}
                        dropdown_label={meta.dropdown_label}
                        dropdown_options={dropdownOptions}
                        isMultiSelect={isMultiSelect}
                        markdownContent={markdownContent}
                        setMarkdownContent={setMarkdownContent}
                        onDropDownSelection={onDropDownSelection}
                        versionOptions={versionOptions}
                        version={version}
                        setVersion={setVersion}
                        onNext={onNext}
                        onSave={onSave}
                        nextLabel={nextLabel}
                        setLoading={setLoading}
                        originalMarkdownContent={originalMarkdownContent}
                    />
                </Box>
            )}
        </Box>
    );
}
